import {ID} from '@wandb/weave/common/util/id';
import React, {useCallback, useMemo, useRef} from 'react';
import {twMerge} from 'tailwind-merge';

import {XAxisFormatValues} from '../../../../generated/graphql';
import {type SettingType} from '../../../../services/analytics/workspaceSettingsEvents';
import {
  calculateIsSettingEnabled,
  type SettingOverrideCounts,
} from '../../../../state/views/workspaceSettings/settingOverrideUtils';
import {globalXAxisOptions} from '../../../../util/runhelpers';
import {useKeyInfoQueryContext} from '../../../MultiRunWorkspace/KeyInfoQueryContext';
import {ClearSettingsRows} from '../components/ClearSettingsRow';
import {ResetSettingButton} from '../components/ResetSettingButton';
import {SettingIndentedRow} from '../components/SettingIndentedRow';
import {SettingLabel} from '../components/SettingLabel';
import {SettingSelect} from '../components/SettingSelect';
import {useClearSettingRows} from '../hooks/useClearSettingRows';
import {type ClearSettingFnType} from '../hooks/useSettingOverrides';
import {
  type LinePlotSettings,
  type SettingLevel,
  type SettingSelectOptionType,
} from '../types';
import {xAxisLabel} from '../utils';
import {
  DEFAULT_X_AXIS_OPTION,
  DEFAULT_X_AXIS_OPTION_VALUES,
} from './linePlotDefaults';
import {XAxisFormat, xAxisIsBuiltin} from './XAxisFormat';
import {XAxisRange} from './XAxisRange';

const EMPTY_X_AXIS_SETTINGS = {
  xAxis: undefined,
  xAxisMin: undefined,
  xAxisMax: undefined,
  xLogScale: undefined,
  xAxisFormat: undefined,
};

export type LinePlotXAxisProps = {
  clearSetting: ClearSettingFnType;

  disabled?: boolean;
  /**
   * These are the values that are displayed to the user. We want to show the
   * inherited values when relevant.
   */
  displayValues: {
    xAxis: string;
    xAxisMax: number | undefined;
    xAxisMin: number | undefined;
    xLogScale: boolean; // we need a value to feed into the toggle comp
    xAxisFormat?: XAxisFormatValues;
  };
  numOverrides?: SettingOverrideCounts;
  settingLevel: SettingLevel;
  /**
   * These are the values that match in the view spec depending on the setting level.
   */
  values: {
    xAxis?: string | undefined;
    xAxisMax?: number | null | undefined;
    xAxisMin?: number | null | undefined;
    xLogScale?: boolean | undefined; // this can be undefined to indicate user hasn't touched the setting
    xAxisFormat?: XAxisFormatValues;
  };
  useXAxisOptions?: () => SettingSelectOptionType[];
  trackSetting: (setting: SettingType, value: string | undefined) => void;
  updateSetting: (settings: Partial<LinePlotSettings>) => void;
};

export const LinePlotXAxis = ({
  clearSetting,
  disabled,
  numOverrides,
  settingLevel,
  trackSetting,
  updateSetting,
  useXAxisOptions,
  values,
  displayValues,
}: LinePlotXAxisProps) => {
  const ref = useRef({
    xAxisSelectorId: 'x-axis-selector-' + ID(),
  });

  useXAxisOptions = useXAxisOptions ?? useWorkspaceXAxisOptions;
  const xAxisOptions = useXAxisOptions();

  const xAxisValue = useMemo(() => {
    // X axis options in a workspace vs reports are different.
    // If a user tries to create a report from the workspace page,
    // we need to check if the currently selected xAxis is valid in reports.
    const isValidXAxis = (xAxisOptions ?? []).some(
      opt => opt.value === displayValues.xAxis
    );
    if (isValidXAxis && displayValues.xAxis != null) {
      return {
        value: displayValues.xAxis,
        label: xAxisLabel(displayValues.xAxis),
      };
    }
    return DEFAULT_X_AXIS_OPTION;
  }, [displayValues.xAxis, xAxisOptions]);

  const onXAxisChange = useCallback(
    (selected: SettingSelectOptionType | null) => {
      let xAxisFormat: XAxisFormatValues = XAxisFormatValues.Number;
      if (selected != null) {
        if (selected.value === '_timestamp') {
          xAxisFormat = XAxisFormatValues.Datetime;
        }
        updateSetting({
          xAxis: selected.value,
          xAxisFormat,
        });
        trackSetting('xAxis', selected.value.toString());
      }
    },
    [updateSetting, trackSetting]
  );

  const clearSettingRows = useClearSettingRows({
    clearSetting,
    emptySettings: EMPTY_X_AXIS_SETTINGS,
    numOverrides,
    settingKey: 'xAxis',
    settingLevel,
  });
  return (
    <div
      className={twMerge('flex flex-col', 'gap-8')}
      data-test="x-axis-setting">
      {settingLevel !== 'panel' ? (
        <div className="flex w-full">
          <SettingLabel
            text="X axis"
            htmlFor={ref.current.xAxisSelectorId}
            className="shrink-0"
          />
          <div className="item-center flex w-full flex-row gap-8">
            <SettingSelect
              id={ref.current.xAxisSelectorId}
              className="grow"
              options={xAxisOptions}
              value={xAxisValue}
              onChange={onXAxisChange}
            />
            <ResetSettingButton
              isEnabled={calculateIsSettingEnabled('xAxis', values)} // important that we are looking at value is spec at settingLevel rather than the displayValues which may be inherited from parent
              onButtonClick={() =>
                clearSetting(settingLevel, 'xAxis', EMPTY_X_AXIS_SETTINGS)
              }
              settingLevel={settingLevel}
            />
          </div>
        </div>
      ) : null}
      <div className="flex">
        <SettingIndentedRow>
          {!xAxisIsBuiltin(xAxisValue.value) ? (
            <XAxisFormat
              trackSetting={trackSetting}
              updateSetting={updateSetting}
              xAxisFormat={displayValues.xAxisFormat}
            />
          ) : null}
          <XAxisRange
            disabled={disabled}
            trackSetting={trackSetting}
            settingLevel={settingLevel}
            updateSetting={updateSetting}
            xAxisMax={displayValues.xAxisMax}
            xAxisMin={displayValues.xAxisMin}
            xLogScale={displayValues.xLogScale}
            labelRequired={settingLevel !== 'panel'}
          />
        </SettingIndentedRow>
      </div>
      <ClearSettingsRows rows={clearSettingRows} />
    </div>
  );
};

export const useWorkspaceXAxisOptions = () => {
  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();
  const historyKeyXAxis = useMemo(
    () => globalXAxisOptions(historyKeyInfo),
    [historyKeyInfo]
  );

  const xValues = useMemo(() => {
    return DEFAULT_X_AXIS_OPTION_VALUES.concat(historyKeyXAxis).map(val => ({
      label: xAxisLabel(val),
      value: val,
    }));
  }, [historyKeyXAxis]);

  return xValues;
};

// Reports support multiple runsets, so default options
export const getReportXAxisOptions = (): SettingSelectOptionType[] => {
  return DEFAULT_X_AXIS_OPTION_VALUES.map(val => ({
    label: xAxisLabel(val),
    value: val,
  }));
};
